import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { complianceColor, complianceLabel } from '../components/shared/theme';
import { inputStyle, bigBtn, smallBtn, formOverlay, formContainer, sectionLabel, card, fieldRow, fieldLabel, fieldVal, inlineInput, tableStyle, thStyle, trStyle } from '../lib/styles';

const SERVICE_TYPES = [
  { name:'Oil Change',            interval_miles:10000, interval_days:90 },
  { name:'Oil Filter',            interval_miles:10000, interval_days:90 },
  { name:'Tire Rotation',         interval_miles:15000, interval_days:180 },
  { name:'Brake Inspection',      interval_miles:25000, interval_days:365 },
  { name:'Annual DOT Inspection', interval_miles:null,  interval_days:365 },
  { name:'Air Filter',            interval_miles:25000, interval_days:365 },
  { name:'Fuel Filter',           interval_miles:30000, interval_days:null },
  { name:'Coolant Flush',         interval_miles:100000,interval_days:730 },
  { name:'Transmission Service',  interval_miles:50000, interval_days:null },
  { name:'DPF Cleaning',          interval_miles:150000,interval_days:null },
  { name:'Tire Replacement',      interval_miles:null,  interval_days:null },
  { name:'Electrical',            interval_miles:null,  interval_days:null },
  { name:'AC / Heat',             interval_miles:null,  interval_days:null },
  { name:'Engine Repair',         interval_miles:null,  interval_days:null },
  { name:'Other',                 interval_miles:null,  interval_days:null },
];

interface ServiceItem { name: string; cost: number; }

const emptyForm = {
  asset_id: "",
  unit_number: "",
  service_date: "",
  odometer_at_service: 0,
  performed_by: "VENDOR" as "IN_HOUSE"|"VENDOR"|"DEALER",
  vendor_name: "",
  vendor_phone: "",
  vendor_address: "",
  invoice_number: "",
  next_service_date: "",
  next_service_odometer: 0,
  warranty_expiration: "",
  notes: "",
};

interface MaintenanceHubProps {
  data: any[];
  assets: any[];
  companyId: string | null;
  newMaintenance: any;
  setNewMaintenance: (v: any) => void;
  showEntryForm: boolean;
  setShowEntryForm: (v: boolean) => void;
  fetchRoomData: () => void;
}

const today = new Date();
const addDays = (d: Date, n: number) => { const r = new Date(d); r.setDate(r.getDate() + n); return r.toISOString().split('T')[0]; };
const daysBetween = (a: Date, b: string) => Math.floor((new Date(b).getTime() - a.getTime()) / 86400000);

const svcStatus = (records: any[], asset: any) => {
  const open = records.filter(r => r.status !== 'COMPLETED');
  if (open.length === 0) return 'UP TO DATE';
  const overdue = open.some(r => (r.next_service_date && new Date(r.next_service_date) < today) || (r.next_service_odometer && asset?.odometer && r.next_service_odometer <= asset.odometer));
  if (overdue) return 'OVERDUE';
  const dueSoon = open.some(r => {
    if (r.next_service_date) { const d = daysBetween(today, r.next_service_date); if (d <= 14) return true; }
    if (r.next_service_odometer && asset?.odometer) { const m = r.next_service_odometer - asset.odometer; if (m <= 500) return true; }
    return false;
  });
  return dueSoon ? 'DUE SOON' : 'UP TO DATE';
};

const statusColor = (s: string) => s === 'OVERDUE' ? '#ff4444' : s === 'DUE SOON' ? '#FFD700' : '#32CD32';

export default function MaintenanceHub({ data, assets, companyId, showEntryForm, setShowEntryForm, fetchRoomData }: MaintenanceHubProps) {
  const [viewMode, setViewMode] = useState<'unit'|'record'>('unit');
  const [selectedAsset, setSelectedAsset] = useState<any>(null);
  const [unitRecords, setUnitRecords] = useState<any[]>([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [typeFilter, setTypeFilter] = useState("ALL");
  const [form, setForm] = useState({ ...emptyForm });
  const [serviceItems, setServiceItems] = useState<ServiceItem[]>([{ name:'Oil Change', cost:0 }]);
  const [showOdoModal, setShowOdoModal] = useState(false);
  const [odoAsset, setOdoAsset] = useState<any>(null);
  const [odoValue, setOdoValue] = useState(0);
  const [saving, setSaving] = useState(false);

  const fetchUnitRecords = useCallback(async (assetId: string) => {
    const { data: rows } = await supabase.from('maintenance').select('*').eq('asset_id', assetId).order('created_at', { ascending: false });
    setUnitRecords(rows || []);
  }, []);

  useEffect(() => {
    if (selectedAsset?.id) fetchUnitRecords(selectedAsset.id);
  }, [selectedAsset, fetchUnitRecords]);

  const openAsset = (asset: any) => {
    setSelectedAsset(asset);
    setUnitRecords([]);
  };

  const handleServiceItemChange = (idx: number, field: 'name'|'cost', value: string|number) => {
    const updated = [...serviceItems];
    if (field === 'name') {
      const svc = SERVICE_TYPES.find(s => s.name === value);
      updated[idx] = { ...updated[idx], name: value as string };
      // Auto-fill next service date/odometer from interval
      if (svc && form.odometer_at_service) {
        if (svc.interval_miles) setForm(f => ({ ...f, next_service_odometer: f.odometer_at_service + svc.interval_miles! }));
        if (svc.interval_days && form.service_date) setForm(f => ({ ...f, next_service_date: addDays(new Date(f.service_date), svc.interval_days!) }));
      }
    } else {
      updated[idx] = { ...updated[idx], cost: Number(value) };
    }
    setServiceItems(updated);
  };

  const totalItemsCost = serviceItems.reduce((s, i) => s + Number(i.cost || 0), 0);

  const handleSave = async () => {
    if (!companyId) { alert("Company not loaded."); return; }
    if (!form.unit_number) { alert("Select a unit."); return; }
    if (!form.service_date) { alert("Service date is required."); return; }
    if (!form.odometer_at_service) { alert("Odometer reading is required."); return; }
    setSaving(true);

    const asset = assets.find(a => a.unit_number === form.unit_number);
    const isDOT = serviceItems.some(i => i.name === 'Annual DOT Inspection');

    const payload: any = {
      company_id: companyId,
      unit_number: form.unit_number,
      asset_id: asset?.id || null,
      service_type: serviceItems.map(i => i.name).join(', '),
      service_items: serviceItems,
      last_service: form.service_date,
      next_service: form.next_service_date || null,
      next_service_date: form.next_service_date || null,
      next_service_odometer: form.next_service_odometer || null,
      odometer_at_service: form.odometer_at_service,
      mileage_at_service: form.odometer_at_service,
      cost: totalItemsCost,
      total_cost: totalItemsCost,
      performed_by: form.performed_by,
      vendor: form.vendor_name || null,
      vendor_name: form.vendor_name || null,
      vendor_phone: form.vendor_phone || null,
      vendor_address: form.vendor_address || null,
      invoice_number: form.invoice_number || null,
      warranty_expiration: form.warranty_expiration || null,
      notes: form.notes || null,
      status: 'COMPLETED',
    };

    const { error: mainErr } = await supabase.from('maintenance').insert(payload);
    if (mainErr) { alert(`Save failed: ${mainErr.message}`); setSaving(false); return; }

    // Update asset odometer
    if (asset?.id) {
      const assetUpdate: any = { odometer: form.odometer_at_service };
      if (isDOT && form.service_date) {
        assetUpdate.last_inspection_date = form.service_date;
        assetUpdate.next_inspection_due = form.next_service_date || null;
      }
      await supabase.from('assets').update(assetUpdate).eq('id', asset.id);
    }

    setShowEntryForm(false);
    setForm({ ...emptyForm });
    setServiceItems([{ name:'Oil Change', cost:0 }]);
    fetchRoomData();
    if (selectedAsset?.id) {
      fetchUnitRecords(selectedAsset.id);
    } else {
      setViewMode('record');
    }
    setSaving(false);
  };

  const handleOdoSave = async () => {
    if (!odoAsset?.id || !odoValue) return;
    await supabase.from('assets').update({ odometer: odoValue }).eq('id', odoAsset.id);
    setShowOdoModal(false);
    setOdoAsset(null);
    setOdoValue(0);
    fetchRoomData();
    if (selectedAsset?.id === odoAsset.id) setSelectedAsset({ ...selectedAsset, odometer: odoValue });
  };

  // ── COST SUMMARY ──
  const costSummary = (records: any[]) => {
    const now = new Date();
    const cut30 = new Date(now); cut30.setDate(now.getDate() - 30);
    const cut90 = new Date(now); cut90.setDate(now.getDate() - 90);
    const cut365 = new Date(now); cut365.setDate(now.getDate() - 365);
    const cost = (recs: any[]) => recs.reduce((s: number, r: any) => s + Number(r.total_cost || r.cost || 0), 0);
    const c30 = cost(records.filter(r => r.last_service && new Date(r.last_service) >= cut30));
    const c90 = cost(records.filter(r => r.last_service && new Date(r.last_service) >= cut90));
    const c365 = cost(records.filter(r => r.last_service && new Date(r.last_service) >= cut365));
    const cAll = cost(records);
    return { c30, c90, c365, cAll };
  };

  const fi: React.CSSProperties = { background:"#0a0a0a", border:"1px solid #2a2a2a", color:"#fff", padding:"6px 10px", fontFamily:"monospace", fontSize:"11px" };

  return (
    <>
      {/* ── ODOMETER MODAL ── */}
      {showOdoModal && (
        <div style={formOverlay}>
          <div style={{ ...formContainer, width:"380px" }}>
            <h3 style={{ color:"#32CD32", marginTop:0 }}>UPDATE ODOMETER</h3>
            <div style={{ color:"#aaa", fontSize:"12px", marginBottom:"12px" }}>Unit: <strong style={{ color:"#fff" }}>{odoAsset?.unit_number}</strong> — Current: <strong style={{ color:"#FFD700" }}>{(odoAsset?.odometer||0).toLocaleString()} mi</strong></div>
            <input type="number" placeholder="New odometer reading" style={inputStyle} value={odoValue||""} onChange={e=>setOdoValue(Number(e.target.value))} />
            <div style={{ display:"flex", gap:"10px", marginTop:"12px" }}>
              <button onClick={handleOdoSave} style={bigBtn}>SAVE</button>
              <button onClick={()=>setShowOdoModal(false)} style={{...bigBtn,background:"#333",color:"#fff"}}>CANCEL</button>
            </div>
          </div>
        </div>
      )}

      {/* ── LOG NEW SERVICE FORM ── */}
      {showEntryForm && (
        <div style={formOverlay}>
          <div style={{ ...formContainer, width:"620px", maxHeight:"90vh", overflowY:"auto" }}>
            <h3 style={{ color:"#32CD32", marginTop:0 }}>LOG SERVICE</h3>

            <div style={sectionLabel}>UNIT SELECTION</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
              <select style={inputStyle as any} value={form.unit_number} onChange={e=>{
                const asset = assets.find(a=>a.unit_number===e.target.value);
                setForm({...form, unit_number:e.target.value, asset_id:asset?.id||"", odometer_at_service:asset?.odometer||0});
              }}>
                <option value="">Select Unit *</option>
                {assets.map(a=><option key={a.id} value={a.unit_number}>{a.unit_number} — {a.make_model||a.asset_type||''} ({(a.odometer||0).toLocaleString()} mi)</option>)}
              </select>
              <div style={{ display:"flex", gap:"10px" }}>
                <div style={{ flex:1 }}><div style={{ color:"#777", fontSize:"11px", marginBottom:"4px" }}>Service Date *</div><input type="date" style={inputStyle} value={form.service_date} onChange={e=>setForm({...form,service_date:e.target.value})} /></div>
              </div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"10px" }}>
              <div><div style={{ color:"#777", fontSize:"11px", marginBottom:"4px" }}>Odometer at Service *</div><input type="number" style={inputStyle} value={form.odometer_at_service||""} onChange={e=>setForm({...form,odometer_at_service:Number(e.target.value)})} /></div>
              <select style={inputStyle as any} value={form.performed_by} onChange={e=>setForm({...form,performed_by:e.target.value as any})}>
                <option value="IN_HOUSE">In House</option>
                <option value="VENDOR">Vendor / Shop</option>
                <option value="DEALER">Dealer</option>
              </select>
              <input type="text" placeholder="Invoice #" style={inputStyle} value={form.invoice_number} onChange={e=>setForm({...form,invoice_number:e.target.value})} />
            </div>

            {(form.performed_by === 'VENDOR' || form.performed_by === 'DEALER') && (
              <>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
                  <input type="text" placeholder="Vendor / Shop Name" style={inputStyle} value={form.vendor_name} onChange={e=>setForm({...form,vendor_name:e.target.value})} />
                  <input type="text" placeholder="Vendor Phone" style={inputStyle} value={form.vendor_phone} onChange={e=>setForm({...form,vendor_phone:e.target.value})} />
                </div>
                <input type="text" placeholder="Vendor Address" style={inputStyle} value={form.vendor_address} onChange={e=>setForm({...form,vendor_address:e.target.value})} />
              </>
            )}

            <div style={sectionLabel}>SERVICE ITEMS</div>
            {serviceItems.map((item,idx)=>(
              <div key={idx} style={{ display:"grid", gridTemplateColumns:"1fr 120px 36px", gap:"8px", marginBottom:"8px", alignItems:"center" }}>
                <select style={inputStyle as any} value={item.name} onChange={e=>handleServiceItemChange(idx,'name',e.target.value)}>
                  {SERVICE_TYPES.map(s=><option key={s.name} value={s.name}>{s.name}</option>)}
                </select>
                <input type="number" placeholder="Cost ($)" style={inputStyle} value={item.cost||""} onChange={e=>handleServiceItemChange(idx,'cost',e.target.value)} />
                <button type="button" onClick={()=>setServiceItems(serviceItems.filter((_,i)=>i!==idx))} style={{ background:"transparent", border:"1px solid #333", color:"#ff4444", cursor:"pointer", fontFamily:"monospace", fontSize:"14px", padding:"4px" }}>×</button>
              </div>
            ))}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"12px" }}>
              <button type="button" onClick={()=>setServiceItems([...serviceItems,{name:'Oil Change',cost:0}])} style={{ background:"transparent", border:"1px solid #32CD32", color:"#32CD32", padding:"4px 12px", fontFamily:"monospace", fontSize:"11px", cursor:"pointer" }}>+ ADD ITEM</button>
              <div style={{ color:"#32CD32", fontWeight:700, fontSize:"13px" }}>TOTAL: ${totalItemsCost.toFixed(2)}</div>
            </div>

            <div style={sectionLabel}>NEXT SERVICE</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px" }}>
              <div><div style={{ color:"#777", fontSize:"11px", marginBottom:"4px" }}>Next Service Date</div><input type="date" style={inputStyle} value={form.next_service_date} onChange={e=>setForm({...form,next_service_date:e.target.value})} /></div>
              <div><div style={{ color:"#777", fontSize:"11px", marginBottom:"4px" }}>Next Service Odometer</div><input type="number" style={inputStyle} value={form.next_service_odometer||""} onChange={e=>setForm({...form,next_service_odometer:Number(e.target.value)})} /></div>
            </div>
            <div><div style={{ color:"#777", fontSize:"11px", marginBottom:"4px" }}>Warranty Expiration</div><input type="date" style={inputStyle} value={form.warranty_expiration} onChange={e=>setForm({...form,warranty_expiration:e.target.value})} /></div>
            <textarea placeholder="Notes..." style={{...inputStyle,resize:"vertical"} as any} rows={2} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} />

            <div style={{ display:"flex", gap:"10px", marginTop:"10px" }}>
              <button onClick={handleSave} disabled={saving} style={bigBtn}>{saving?"SAVING...":"SAVE SERVICE RECORD"}</button>
              <button onClick={()=>{setShowEntryForm(false);setForm({...emptyForm});setServiceItems([{name:'Oil Change',cost:0}]);}} style={{...bigBtn,background:"#333",color:"#fff"}}>CANCEL</button>
            </div>
          </div>
        </div>
      )}

      {/* ── UNIT DETAIL VIEW ── */}
      {selectedAsset && (
        <div style={card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px" }}>
            <button onClick={()=>{setSelectedAsset(null);setUnitRecords([]);}} style={{ color:"#32CD32", background:"none", border:"none", cursor:"pointer", fontSize:"13px" }}>{"← BACK TO FLEET"}</button>
            <div style={{ display:"flex", gap:"8px" }}>
              <button onClick={()=>{
                setForm({...emptyForm, unit_number:selectedAsset.unit_number, asset_id:selectedAsset.id, odometer_at_service:selectedAsset.odometer||0});
                setShowEntryForm(true);
              }} style={smallBtn}>+ LOG SERVICE</button>
              <button onClick={()=>{setOdoAsset(selectedAsset);setOdoValue(selectedAsset.odometer||0);setShowOdoModal(true);}} style={{...smallBtn,background:"#1a1a1a",border:"1px solid #FFD700",color:"#FFD700"}}>UPDATE ODOMETER</button>
            </div>
          </div>
          <div style={{ marginBottom:"16px" }}>
            <h3 style={{ margin:"0 0 4px 0" }}>UNIT: {selectedAsset.unit_number} — {selectedAsset.make_model||selectedAsset.asset_type||''}</h3>
            <div style={{ color:"#aaa", fontSize:"12px" }}>VIN: {selectedAsset.vin||'—'} &nbsp;|&nbsp; Odometer: <span style={{ color:"#32CD32", fontWeight:700 }}>{(selectedAsset.odometer||0).toLocaleString()} mi</span></div>
          </div>

          {/* SERVICE STATUS BOARD */}
          <div style={{ border:"1px solid #1e1e1e", padding:"16px", background:"#050505", marginBottom:"16px" }}>
            <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700, marginBottom:"12px" }}>SERVICE STATUS BOARD</div>
            {unitRecords.length === 0 ? <p style={{ color:"#444", fontSize:"12px" }}>No service records yet.</p> : (()=>{
              const seen = new Set<string>();
              const latest: any[] = [];
              for (const r of unitRecords) {
                const types = (r.service_type||'').split(',').map((s:string)=>s.trim());
                for (const t of types) {
                  if (!seen.has(t)) { seen.add(t); latest.push({ ...r, _type:t }); }
                }
              }
              return latest.map((r:any,i:number)=>{
                const milesLeft = r.next_service_odometer && selectedAsset.odometer ? r.next_service_odometer - selectedAsset.odometer : null;
                const daysLeft = r.next_service_date ? daysBetween(today, r.next_service_date) : null;
                const isOverdue = (milesLeft != null && milesLeft <= 0) || (daysLeft != null && daysLeft < 0);
                const isDueSoon = !isOverdue && ((milesLeft != null && milesLeft <= 500) || (daysLeft != null && daysLeft <= 14));
                const statusCol = isOverdue ? '#ff4444' : isDueSoon ? '#FFD700' : '#32CD32';
                const badge = isOverdue ? 'OVERDUE' : isDueSoon ? 'DUE SOON' : 'OK';
                return (
                  <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:"1px solid #0f0f0f" }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:"12px" }}>{r._type}</div>
                      <div style={{ color:"#555", fontSize:"10px", marginTop:"2px" }}>Last: {r.last_service||'—'} {r.odometer_at_service?`@ ${Number(r.odometer_at_service).toLocaleString()} mi`:''}</div>
                    </div>
                    <div style={{ textAlign:"right", fontSize:"11px", color:"#aaa" }}>
                      {r.next_service_date && <div>Next: {r.next_service_date}</div>}
                      {r.next_service_odometer && <div>@ {Number(r.next_service_odometer).toLocaleString()} mi</div>}
                      {milesLeft != null && <div style={{ color:statusCol }}>{milesLeft > 0 ? `${milesLeft.toLocaleString()} mi remaining` : `${Math.abs(milesLeft).toLocaleString()} mi OVERDUE`}</div>}
                      {daysLeft != null && <div style={{ color:statusCol }}>{daysLeft > 0 ? `${daysLeft}d remaining` : `${Math.abs(daysLeft)}d OVERDUE`}</div>}
                    </div>
                    <span style={{ marginLeft:"16px", color:statusCol, fontSize:"10px", fontWeight:700, border:`1px solid ${statusCol}`, padding:"2px 8px", whiteSpace:"nowrap" }}>{badge}</span>
                  </div>
                );
              });
            })()}
          </div>

          {/* COST SUMMARY */}
          {unitRecords.length > 0 && (()=>{
            const { c30, c90, c365, cAll } = costSummary(unitRecords);
            const totalMi = selectedAsset.odometer || 0;
            const cpm = totalMi > 0 && cAll > 0 ? (cAll / totalMi).toFixed(3) : '—';
            return (
              <div style={{ border:"1px solid #1e1e1e", padding:"16px", background:"#050505", marginBottom:"16px" }}>
                <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700, marginBottom:"12px" }}>MAINTENANCE COSTS — {selectedAsset.unit_number}</div>
                <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:"10px" }}>
                  {[{label:"LAST 30 DAYS",val:`$${c30.toFixed(2)}`},{label:"LAST 90 DAYS",val:`$${c90.toFixed(2)}`},{label:"LAST 12 MONTHS",val:`$${c365.toFixed(2)}`},{label:"ALL TIME",val:`$${cAll.toFixed(2)}`},{label:"COST/MILE",val:cpm==='—'?'—':`$${cpm}/mi`}].map((k,i)=>(
                    <div key={i} style={{ background:"#0a0a0a", border:"1px solid #1a1a1a", padding:"10px" }}>
                      <div style={{ color:"#555", fontSize:"9px", letterSpacing:"1px", marginBottom:"4px" }}>{k.label}</div>
                      <div style={{ color:"#32CD32", fontSize:"14px", fontWeight:700 }}>{k.val}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* SERVICE HISTORY */}
          <div style={{ color:"#32CD32", fontSize:"10px", letterSpacing:"2px", fontWeight:700, marginBottom:"12px" }}>SERVICE HISTORY</div>
          {unitRecords.length === 0 ? <p style={{ color:"#444", fontSize:"12px" }}>No records yet.</p> : (
            <table style={tableStyle}>
              <thead><tr>
                <th style={thStyle}>DATE</th><th style={thStyle}>SERVICE</th><th style={thStyle}>ODOMETER</th>
                <th style={thStyle}>VENDOR</th><th style={thStyle}>COST</th><th style={thStyle}>NEXT DUE</th><th style={thStyle}>STATUS</th>
              </tr></thead>
              <tbody>
                {unitRecords.map((r:any,i:number)=>(
                  <tr key={i} style={trStyle}>
                    <td style={{ padding:"8px 12px", color:"#aaa" }}>{r.last_service||"—"}</td>
                    <td style={{ padding:"8px 12px", fontSize:"11px" }}>{r.service_type||"—"}</td>
                    <td style={{ padding:"8px 12px", color:"#aaa" }}>{r.odometer_at_service?`${Number(r.odometer_at_service).toLocaleString()} mi`:"—"}</td>
                    <td style={{ padding:"8px 12px", color:"#aaa", fontSize:"11px" }}>{r.vendor_name||r.vendor||"—"}</td>
                    <td style={{ padding:"8px 12px", color:"#32CD32", fontWeight:700 }}>{r.total_cost||r.cost?`$${Number(r.total_cost||r.cost||0).toFixed(2)}`:"—"}</td>
                    <td style={{ padding:"8px 12px", color:complianceColor(r.next_service_date) }}>{complianceLabel(r.next_service_date)}</td>
                    <td style={{ padding:"8px 12px" }}><span style={{ color:r.status==='COMPLETED'?'#32CD32':'#FFD700', fontSize:"10px" }}>{r.status||"—"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ── MAIN LIST (BY UNIT / BY RECORD) ── */}
      {!selectedAsset && (
        <div>
          {/* Mode tabs */}
          <div style={{ display:"flex", gap:"0", marginBottom:"16px", borderBottom:"1px solid #1e1e1e" }}>
            {(['unit','record'] as const).map(m=>(
              <button key={m} onClick={()=>setViewMode(m)}
                style={{ padding:"8px 20px", fontFamily:"monospace", fontSize:"11px", fontWeight:700, cursor:"pointer", border:"none", borderBottom:viewMode===m?"2px solid #32CD32":"2px solid transparent", background:"transparent", color:viewMode===m?"#32CD32":"#555" }}>
                {m === 'unit' ? 'BY UNIT' : 'BY RECORD'}
              </button>
            ))}
          </div>

          {/* BY UNIT */}
          {viewMode === 'unit' && (()=>{
            const assetList = assets;
            return (
              <table style={tableStyle}>
                <thead><tr>
                  <th style={thStyle}>UNIT #</th><th style={thStyle}>MAKE / MODEL</th><th style={thStyle}>ODOMETER</th>
                  <th style={thStyle}>LAST SERVICE</th><th style={thStyle}>LAST TYPE</th>
                  <th style={thStyle}>NEXT DUE</th><th style={thStyle}>MILES UNTIL NEXT</th>
                  <th style={thStyle}>STATUS</th><th style={thStyle}>ACTION</th>
                </tr></thead>
                <tbody>
                  {assetList.length === 0 ? <tr><td colSpan={9} style={{ padding:"16px", color:"#444" }}>No assets found.</td></tr> :
                  assetList.map((asset:any,i:number)=>{
                    const recs = data.filter(r => r.unit_number === asset.unit_number || r.asset_id === asset.id);
                    const lastRec = recs.length > 0 ? recs.reduce((a:any,b:any)=>new Date(a.last_service||0)>new Date(b.last_service||0)?a:b) : null;
                    const nextDate = recs.filter(r=>r.next_service_date&&r.status!=='COMPLETED').map(r=>r.next_service_date).sort()[0];
                    const nextOdo = recs.filter(r=>r.next_service_odometer&&r.status!=='COMPLETED').map(r=>r.next_service_odometer).sort((a:number,b:number)=>a-b)[0];
                    const milesUntil = nextOdo && asset.odometer ? nextOdo - asset.odometer : null;
                    const status = svcStatus(recs, asset);
                    const sc = statusColor(status);
                    return (
                      <tr key={i} style={trStyle} onMouseEnter={e=>(e.currentTarget as HTMLElement).style.background='#0d0d0d'} onMouseLeave={e=>(e.currentTarget as HTMLElement).style.background=''}>
                        <td style={{ padding:"10px 12px", color:"#32CD32", fontWeight:700, cursor:"pointer" }} onClick={()=>openAsset(asset)}>{asset.unit_number}</td>
                        <td style={{ padding:"10px 12px" }}>{asset.make_model||asset.asset_type||"—"}</td>
                        <td style={{ padding:"10px 12px", color:"#aaa" }}>{asset.odometer?(asset.odometer).toLocaleString()+' mi':"—"}</td>
                        <td style={{ padding:"10px 12px", color:"#aaa", fontSize:"11px" }}>{lastRec?.last_service||"—"}</td>
                        <td style={{ padding:"10px 12px", fontSize:"11px" }}>{lastRec?.service_type||"—"}</td>
                        <td style={{ padding:"10px 12px", color:nextDate?complianceColor(nextDate):'#555' }}>{nextDate?complianceLabel(nextDate):"—"}</td>
                        <td style={{ padding:"10px 12px", color:milesUntil!=null&&milesUntil<=500?'#ff4444':milesUntil!=null&&milesUntil<=2000?'#FFD700':'#aaa' }}>{milesUntil!=null?`${milesUntil.toLocaleString()} mi`:"—"}</td>
                        <td style={{ padding:"10px 12px" }}><span style={{ color:sc, fontSize:"10px", fontWeight:700, border:`1px solid ${sc}`, padding:"2px 6px" }}>{status}</span></td>
                        <td style={{ padding:"10px 12px" }}><button onClick={()=>openAsset(asset)} style={smallBtn}>VIEW</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            );
          })()}

          {/* BY RECORD */}
          {viewMode === 'record' && (()=>{
            const q = search.toLowerCase();
            const rows = data.filter(item => {
              if (q && !(item.unit_number||'').toLowerCase().includes(q)) return false;
              if (statusFilter !== 'ALL' && (item.status||'').toUpperCase() !== statusFilter) return false;
              if (typeFilter !== 'ALL' && !(item.service_type||'').includes(typeFilter)) return false;
              return true;
            });
            const isOverdue = (d:string) => d && new Date(d) < today;
            return (
            <div>
              <div style={{ display:"flex", gap:"10px", marginBottom:"14px", alignItems:"center", flexWrap:"wrap" }}>
                <input placeholder="Search unit #..." value={search} onChange={e=>setSearch(e.target.value)} style={{ ...fi, width:"180px" }} />
                <select value={statusFilter} onChange={e=>setStatusFilter(e.target.value)} style={fi as any}>
                  {['ALL','COMPLETED','SCHEDULED','IN PROGRESS'].map(s=><option key={s} value={s}>{s==='ALL'?'ALL STATUSES':s}</option>)}
                </select>
                <select value={typeFilter} onChange={e=>setTypeFilter(e.target.value)} style={fi as any}>
                  <option value="ALL">ALL TYPES</option>
                  {SERVICE_TYPES.map(t=><option key={t.name} value={t.name}>{t.name}</option>)}
                </select>
                <span style={{ color:"#555", fontSize:"11px", marginLeft:"auto" }}>{rows.length} record{rows.length!==1?'s':''}</span>
              </div>
              <table style={tableStyle}>
                <thead><tr>
                  <th style={thStyle}>UNIT #</th><th style={thStyle}>SERVICE TYPE</th>
                  <th style={thStyle}>SERVICE DATE</th><th style={thStyle}>NEXT DUE</th>
                  <th style={thStyle}>VENDOR</th><th style={thStyle}>COST</th><th style={thStyle}>STATUS</th>
                </tr></thead>
                <tbody>
                  {rows.map((r:any,i:number)=>(
                    <tr key={i} style={trStyle}>
                      <td style={{ padding:"10px 12px", color:"#32CD32", fontWeight:700 }}>{r.unit_number||"—"}</td>
                      <td style={{ padding:"10px 12px" }}>{r.service_type||"—"}</td>
                      <td style={{ padding:"10px 12px", color:"#aaa" }}>{r.last_service||"—"}</td>
                      <td style={{ padding:"10px 12px", color:isOverdue(r.next_service_date)?'#ff4444':complianceColor(r.next_service_date) }}>{isOverdue(r.next_service_date)?`OVERDUE (${r.next_service_date})`:complianceLabel(r.next_service_date)}</td>
                      <td style={{ padding:"10px 12px", color:"#aaa", fontSize:"11px" }}>{r.vendor_name||r.vendor||"—"}</td>
                      <td style={{ padding:"10px 12px", color:"#32CD32" }}>{r.total_cost||r.cost?`$${Number(r.total_cost||r.cost||0).toFixed(2)}`:"—"}</td>
                      <td style={{ padding:"10px 12px" }}><span style={{ color:r.status==='COMPLETED'?'#32CD32':r.status==='IN PROGRESS'?'#FFD700':'#777', fontSize:"10px" }}>{r.status||"—"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            );
          })()}
        </div>
      )}
    </>
  );
}
